import { UsersRow } from "@outschool/db-queries";
import { isValidEmail, joinCommasAnd, normalizeString } from "@outschool/text";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { DEFAULT_PARENT_NAME } from "@outschool/user";
import emojiStrip from "emoji-strip";
import _ from "lodash";

import * as Env from "./Env";
import * as Geocoder from "./Geocoder";

export const MIN_LEARNER_AGE = 1;
export const MAX_LEARNER_AGE = 18;

export {
  /**
   * @deprecated use import { DEFAULT_PARENT_NAME } from `@outschool/user`
   */
  DEFAULT_PARENT_NAME,
};

export const NAME_MAX_LENGTH = 50;

export function isDeleted(user: $TSFixMe) {
  return user && !!user.deleted_at;
}

export function stripEmojis(str: string, fallback = "Parent") {
  const newName = emojiStrip(str).trim();
  return Boolean(newName) ? newName : fallback;
}

/**
 *
 * @param name the name to split
 * @param noInitial if true, exclude the initial for the last name
 */
export function publicName(user: $TSFixMe, noInitial?: boolean) {
  if (
    !user.name ||
    !user.name.trim() ||
    (user.details && user.details.socialOptOut) ||
    isDeleted(user)
  ) {
    return "";
  }

  if (isSellerOrg(user)) {
    return user.name;
  }

  // Split on any whitespace character (spaces, non-breaking spaces, tabs, etc.)
  const words = user.name.trim().split(/\s+/);
  if (words.length > 1) {
    // For names with multiple words, join all except the last word as the first part,
    // then use the last word's first character as the initial
    const firstPartWords = words.length > 2 ? words.slice(0, -1) : [words[0]];
    const firstPartFormatted = firstPartWords
      .map((word: string) => startCase(word, true))
      .join(" ");
    const lastInitial = words[words.length - 1][0];
    if (noInitial) {
      return firstPartFormatted;
    } else {
      return firstPartFormatted + " " + startCase(lastInitial) + ".";
    }
  } else {
    // Single-word name
    return startCase(words[0], true);
  }
}

export function shortenedLeaderName(leaderName: $TSFixMe) {
  if (!leaderName) {
    return "";
  }
  if (leaderName.length <= 30) {
    return leaderName;
  }
  return leaderName.slice(0, 30).concat("...");
}

/**
 * @param word the word to title case
 * @param capitalizeHyphenatedWords if true, capitalize each part of hyphenated words
 */
function startCase(word: string, capitalizeHyphenatedWords = false) {
  if (word.length === 0) {
    return "";
  } else if (word.length === 1) {
    return word.toLocaleUpperCase();
  }
  // Handle hyphenated words by applying title case to each part
  if (capitalizeHyphenatedWords && word.includes("-")) {
    return word
      .split("-")
      .map((part: string) => {
        if (part.length === 0) {
          return "";
        }
        return part[0].toLocaleUpperCase() + part.slice(1).toLocaleLowerCase();
      })
      .join("-");
  }
  return word[0].toLocaleUpperCase() + word.slice(1).toLocaleLowerCase();
}

export function parentName(user: $TSFixMe, noInitial?: $TSFixMe) {
  const name = publicName(user, noInitial);
  if (name === "" || isDeleted(user)) {
    return DEFAULT_PARENT_NAME;
  }
  return name;
}

export function senderName(user: $TSFixMe) {
  return (user && (user.name || user.email)) || "";
}

export function isEducator(user: $TSFixMe) {
  return !!(user && user.leader_subscribed_at);
}

export function isAdmin(user: $TSFixMe) {
  return !!(user && user.is_admin);
}

export function hasRole(user: $TSFixMe, role: $TSFixMe, inherit = true) {
  return Boolean(
    user &&
      ((inherit && isAdmin(user)) ||
        (Array.isArray(user.roles) && user.roles.includes(role))),
  );
}

function locationTimeZone(user: $TSFixMe) {
  return user && user.location && user.location.timeZone;
}

function browserTimeZone(user: $TSFixMe) {
  return Env.isBrowser
    ? Time.guessBrowserTimeZone()
    : user && user.details && user.details.browserTimeZone;
}

export function preferredTimeRanges(user: $TSFixMe) {
  const { details } = user || {};
  if (!details) {
    return [];
  }
  const { timeRangeSchool, timeRangeAfterSchool, timeRangeWeekend } = details;
  if (
    timeRangeSchool !== false &&
    timeRangeAfterSchool !== false &&
    timeRangeWeekend !== false
  ) {
    return [];
  }
  const times = ["School hours", "After school", "Weekend"];
  const timesNotWanted: string[] = [];
  if (timeRangeSchool === false) {
    timesNotWanted.push("School hours");
  }
  if (timeRangeAfterSchool === false) {
    timesNotWanted.push("After school");
  }
  if (timeRangeWeekend === false) {
    timesNotWanted.push("Weekend");
  }
  return _.difference(times, timesNotWanted);
}

export function geoResultToLocation(geoResult: $TSFixMe) {
  return {
    lat: geoResult.geometry.location.lat,
    lng: geoResult.geometry.location.lng,
    city: Geocoder.resultToCity(geoResult),
    state: Geocoder.resultToState(geoResult),
    geo: JSON.stringify(geoResult),
    address: geoResult.formatted_address,
  };
}

export function isEmailConfirmed(user: $TSFixMe) {
  return !!user.confirmed_at;
}

export function stripUserMetadata(user: $TSFixMe) {
  // TODO: use a whitelist here instead
  const safeUser = _.omit(user, [
    "salt",
    "derived_key",
    "created_at",
    "updated_at",
    "chaperone_required",
  ]);
  if (!safeUser.background_checked_at) {
    // Hide the existence of this field unless it is set.
    delete safeUser.background_checked_at;
  }
  safeUser.details = _.omit(user.details, [
    "confirmEmailToken",
    "confirmEmailIssuedAt",
    "changePasswordToken",
    "changePasswordIssuedAt",
    "holdMessages",
  ]);
  return safeUser;
}

export function publicViewOfParent(parent: UsersRow | null) {
  return _.pick(parent, ["uid", "name"]);
}

export function leaderLinkForUser(name: $TSFixMe) {
  return (name && _.words(name).join("-")) || null;
}

export function hasBackgroundCheckInProgress(user: $TSFixMe) {
  return Boolean(
    (!user.background_checked_at && !!user.background_check_started_at) ||
      (user.background_checked_at &&
        user.background_check_started_at &&
        dayjs(user.background_check_started_at).isAfter(
          user.background_checked_at,
        )),
  );
}

type UserBackgroundCheck = {
  background_checked_at: dayjs.ConfigType | null;
};

export function hasBackgroundChecked(user: UserBackgroundCheck) {
  return user.background_checked_at !== null;
}

export function backgroundCheckExpiry(user: UserBackgroundCheck) {
  return !!user.background_checked_at
    ? dayjs(user.background_checked_at).add(
        Env.BACKGROUND_CHECK_EXPIRATION_MONTHS,
        "months",
      )
    : null;
}

export function hasBackgroundCheckExpired(user: UserBackgroundCheck): boolean {
  const expiry = backgroundCheckExpiry(user);
  return !!expiry && expiry.isBefore();
}

export function hasUKBackgroundCheck(user: $TSFixMe) {
  return timeZone(user).includes("Europe/London");
}

export function isBackgroundCheckAboutToExpire(user: UserBackgroundCheck) {
  const expiry = backgroundCheckExpiry(user);
  return !!expiry && expiry.subtract(4, "week").subtract(2, "day").isBefore();
}

export function hasLocation(user: $TSFixMe) {
  return (
    user.location &&
    user.location.city &&
    user.location.lat !== undefined &&
    user.location.lng !== undefined
  );
}

export function cityState(user: $TSFixMe) {
  return user.location && user.location.city && user.location.state
    ? `${user.location.city}, ${user.location.state}`
    : (user.location && user.location.country) || null;
}

export function hasTimePreferences(user: $TSFixMe) {
  // User has time preferences if at least one is true or false.
  // By default they're all null or undefined
  // (but not necessarily absent from user.details)
  return Boolean(
    user.details &&
      (user.details.timeRangeSchool ||
        user.details.timeRangeAfterSchool ||
        user.details.timeRangeWeekend ||
        user.details.timeRangeSchool === false ||
        user.details.timeRangeWeekend === false ||
        user.details.timeRangeAfterSchool === false),
  );
}

export function timeZone(
  user: $TSFixMe,
  defaultTimeZone: string | null | undefined = Time.OUTSCHOOL_TIMEZONE,
) {
  const locationZone = locationTimeZone(user);
  if (Time.zoneIsValid(locationZone)) {
    return locationZone;
  }
  const browserZone = browserTimeZone(user);
  if (Time.zoneIsValid(browserZone)) {
    return browserZone;
  }
  return defaultTimeZone;
}

export function firstAndLastName(user: $TSFixMe) {
  if (isDeleted(user) || !user.name) {
    return [];
  }

  const tokens = user.name.split(" ");
  const firstTokenEndsWithDot = /\.$/.test(tokens[0]); // e.g. "Mrs. Betty"

  // e.g. "Betty" or "Betty Smith
  if (tokens.length <= 2 && !firstTokenEndsWithDot) {
    return [tokens[0], tokens[1]];
  }

  // e.g. "Mrs. Betty Smith" or "Mrs. Betty Smith Jr."
  return [
    `${tokens[0]} ${tokens[1] || ``}`.trim(),
    tokens.length > 2 ? tokens.slice(2).join(" ") : undefined,
  ];
}

/**
 * @deprecated use `User.firstname` from `@outschool/user`
 */
export function firstName(user: $TSFixMe) {
  if (user && user.name && user.name === DEFAULT_PARENT_NAME) {
    return user.name;
  }
  const names = firstAndLastName(user);
  return names && names[0];
}

export function accountText(user: $TSFixMe, isLeader: $TSFixMe) {
  if (user && user.name) {
    return isLeader ? user.name : firstName(user);
  }

  return "Account";
}

export function emailsMatch(email1: $TSFixMe, email2: $TSFixMe) {
  return normalizeString(email1) === normalizeString(email2);
}

export function otherEmailsMatch(userOtherEmails: $TSFixMe, email: $TSFixMe) {
  return Boolean(
    !!userOtherEmails &&
      userOtherEmails.some((e: $TSFixMe) => emailsMatch(e, email)),
  );
}

export function ageString(user: $TSFixMe) {
  return user
    ? user.children
        .map((c: $TSFixMe) => c.age)
        .sort()
        .join(", ")
    : "";
}

export function extractAges(
  learners: { age?: number | null }[] = [],
): number[] {
  const ages: number[] = [];
  for (const learner of learners) {
    const age = learner?.age;
    if (!!age && MIN_LEARNER_AGE <= age && age <= MAX_LEARNER_AGE) {
      ages.push(age);
    }
  }
  return _.sortBy(_.uniq(ages));
}

export function preferredAges(user: $TSFixMe, learners?: $TSFixMe): number[] {
  if (Array.isArray(learners)) {
    return extractAges(learners);
  }
  if (!!user?.children) {
    return extractAges(user.children);
  }
  return [];
}

export function profileCompletenessSteps(user: $TSFixMe) {
  //TODO: Once server rendered site is tagged move this to shared localization page
  return {
    "Sign up": true,
    "Tell us your name": !!(user && user.name),
    "Choose a photo": !!(user && user.photo),
    "Tell us about your learners": user.children.length > 0,
    "Tell us when you can take classes": !!hasTimePreferences(user),
    "Set your location":
      !!(user && user.location) &&
      !_.isEmpty(user.location) &&
      _.some(user.location),
  };
}

export function profileCompletenessPercent(user: $TSFixMe) {
  const steps = profileCompletenessSteps(user);
  const stepValues = _.values(steps);

  return ~~((_.compact(stepValues).length * 100) / stepValues.length);
}

export function profileCompletenessItems(user: $TSFixMe) {
  const steps = profileCompletenessSteps(user);

  return _.reduce<typeof steps, string[]>(
    steps,
    (items, complete, step) => (complete ? items : items.concat([step])),
    [],
  );
}

//Name, headline, about, photo, availability
export function leaderProfileCompletenessSteps(
  user: $TSFixMe,
  includeAuthSteps = true,
) {
  return {
    ...(includeAuthSteps && {
      "Sign up": true,
    }),
    "Tell us your name": !!(user && user.name),
    "Write a headline": !!(user && user.details && user.details.headline),
    "Fill out 'About Me'": !!(user && user.details && user.details.about),
    "Tell us your phone number": !!(user && user.phone),
    [`Choose a ${user && isSellerOrg(user) ? "logo image" : "photo"}`]: !!(
      user && user.photo
    ),
    "Record an intro video": !!(user && user.video),
    "Tell us your location": !!(user && hasLocation(user)),
    "Verify your email address": !!(user && user.confirmed_at),
  };
}

export function leaderProfileCompletenessPercent(
  user: $TSFixMe,
  includeAuthSteps = true,
) {
  const steps = leaderProfileCompletenessSteps(user, includeAuthSteps);
  const stepValues = _.values(steps);

  return ~~((_.compact(stepValues).length * 100) / stepValues.length);
}

export function leaderProfileCompletenessItems(user: $TSFixMe) {
  const steps = leaderProfileCompletenessSteps(user);

  return _.reduce<typeof steps, string[]>(
    steps,
    (items, complete, step) => (complete ? items : items.concat([step])),
    [],
  );
}

/**
 * @deprecated use @outschool/user
 */
export function holdMessagesFrom(user: $TSFixMe) {
  return (
    (user && user.details && user.details.holdMessages) || isSuspended(user)
  );
}

/**
 * @deprecated use @outschool/user
 */
export function isSuspended(user: $TSFixMe) {
  return Boolean(user && user.suspended_at);
}

export function timePreferenceDescription(user: $TSFixMe) {
  const ranges = preferredTimeRanges(user);
  let result =
    joinCommasAnd(ranges)?.toLowerCase() ??
    "school hours, after school, and weekend";
  result = result.replace("weekend", "over the weekend");
  return result;
}

export function hasPayoutEmail(user: $TSFixMe) {
  return !!user.payout_email;
}

export function hasValidPayoutEmail(user: $TSFixMe) {
  return hasPayoutEmail(user) && isValidEmail(user.payout_email);
}

export function isCharterSchoolCreatedUser(user: $TSFixMe) {
  return Boolean(
    user && user.details && !!user.details.createdByCharterAdminUid,
  );
}

export function needsCharterWelcomeEmail(user: $TSFixMe) {
  return Boolean(
    user && user.details && !!user.details.needsCharterWelcomeEmail,
  );
}

export function parentJoinWizardStepValues(
  currentStep: $TSFixMe,
  currentUser: $TSFixMe,
) {
  let steps = ["age", "times"];
  const skipAgeStep =
    currentUser &&
    currentUser.children &&
    currentUser.children.length &&
    currentUser.children.some((c: $TSFixMe) => c.name && c.name.trim() !== "");
  if (skipAgeStep) {
    steps = steps.filter(s => s !== "age");
  }

  const currentStepIndex = steps.indexOf(currentStep);
  const currentStepNumber = currentStepIndex + 1;
  const totalSteps = steps.length;
  const previousStep =
    currentStepIndex > 0 ? steps[currentStepIndex - 1] : undefined;
  const nextStep =
    currentStepIndex < totalSteps - 1 ? steps[currentStepIndex + 1] : undefined;
  return { currentStepNumber, totalSteps, previousStep, nextStep };
}

export function isApprovedTeacher(user: $TSFixMe) {
  return Boolean(
    user &&
      (user.teacher_approved_at ||
        user.teacher_for_seller_org_user_uid ||
        (user.sellerOrg && user.sellerOrg.currentUserIsTeacher)),
  );
}

export function hasTeacherApplication(user: $TSFixMe) {
  return (
    !!user &&
    !isApprovedTeacher(user) &&
    user.latestTeacherApplication &&
    user.latestTeacherApplication.status === "created"
  );
}

export function unreadConversationsCount(user: $TSFixMe) {
  if (user) {
    return (
      user.unreadMessageThreadsCount +
      user.unreadPrivateClassMessageThreadsCount
    );
  } else {
    return 0;
  }
}

export function isOrgTeacher(
  user:
    | { teacher_for_seller_org_user_uid: any }
    | { sellerOrg: { currentUserIsTeacher: boolean | null } | null },
) {
  return Boolean(
    user &&
      (("teacher_for_seller_org_user_uid" in user &&
        user.teacher_for_seller_org_user_uid) ||
        ("sellerOrg" in user &&
          user.sellerOrg &&
          user.sellerOrg.currentUserIsTeacher)),
  );
}

export function isSellerOrg(user: $TSFixMe) {
  return Boolean(
    user &&
      (user.owner_of_seller_org_uid ||
        user.ownerOfSellerOrg ||
        (user.sellerOrg && user.sellerOrg.currentUserIsOwner)),
  );
}

export function isMemberOfSellerOrg(user: $TSFixMe) {
  return isSellerOrg(user) || isOrgTeacher(user);
}

export function hasConfirmedEmailForPricingOffer(
  user: $TSFixMe,
  pricingOffer: $TSFixMe,
) {
  if (!user || !pricingOffer) {
    return false;
  }
  return (
    (user.confirmed_at &&
      user.email?.toLowerCase() ===
        pricingOffer.userOfferEmail?.toLowerCase()) ||
    user.otherEmailDetails.some(
      (emailDetail: $TSFixMe) =>
        emailDetail.email &&
        emailDetail.confirmed_at &&
        pricingOffer.userOfferEmail?.toLowerCase() ===
          emailDetail.email?.toLowerCase(),
    )
  );
}

export function hasUnconfirmedEmailForPricingOffer(
  user: $TSFixMe,
  pricingOffer: $TSFixMe,
) {
  if (!user || !pricingOffer) {
    return false;
  }

  return (
    (!user.confirmed_at &&
      user.email?.toLowerCase() ===
        pricingOffer.userOfferEmail?.toLowerCase()) ||
    user.otherEmailDetails.some(
      (emailDetail: $TSFixMe) =>
        emailDetail.email &&
        !emailDetail.confirmed_at &&
        pricingOffer.userOfferEmail?.toLowerCase() ===
          emailDetail.email?.toLowerCase(),
    )
  );
}

/*
  returns primary email if it is not confirmed, or an "other" unconfirmed email if was referenced via a pricing offer
*/
export function getEmailToConfirmToUseCredit(
  user: $TSFixMe,
  pricingOffer: $TSFixMe,
) {
  const hasUnconfirmedPricingOfferEmail = hasUnconfirmedEmailForPricingOffer(
    user,
    pricingOffer,
  );

  if (!user || (user.confirmed_at && !hasUnconfirmedPricingOfferEmail)) {
    return null;
  }

  return !user.confirmed_at
    ? user.email?.toLowerCase()
    : pricingOffer?.userOfferEmail?.toLowerCase();
}

export function standardNormalize(row: $TSFixMe) {
  return row && { ...row, email: normalizeString(row.email) };
}

export function getTotalBufferMinutes(buffer: any): number {
  if (!buffer) {
    return 0;
  }

  const hours = buffer.hours ? buffer.hours : 0;
  const minutes = buffer.minutes ? buffer.minutes : 0;

  return hours * 60 + minutes;
}

export function isNewUser(user: Pick<UsersRow, "created_at">) {
  return dayjs().subtract(2, "hours").isBefore(user.created_at);
}
